import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { HostedLayoutTypeResponseValidationSchema, PageTypeEnumValidationSchema } from './enum';
import { ThemeOverridesResponseValidationSchema } from '../theme/themeOverrides';
import { MediaResponseValidationSchema } from './common/file';
import { FieldResponseValidationSchema } from '../field/response';
import { PageFormLayoutResponseValidationSchema } from './formLayout';
import { FunnelStepInputValidationSchema } from './common/funnel';
import { RedirectAndConfirmationResponseValidationSchema } from './common/redirectAndConfirmation';
import { NotificationFieldsResponseValidationSchema } from './common/notification';

export const SharedEventAndFormResponseValidationSchema = z.object({
  title: z.string().nullish().meta({
    description: 'Display title shown on the page.',
  }),
  description: z.string().nullish().meta({
    description: 'Page description stored as Lexical rich text JSON.',
  }),
  descriptionHtml: z.string().nullish().meta({
    description: 'HTML description of the page.',
  }),
});

export const SharedPageResponseValidationSchema = z
  .object({
    id: ObjectIdStringValidationSchema,
    sellerId: ObjectIdStringValidationSchema,
    type: PageTypeEnumValidationSchema,
    name: z.string().nullish().meta({
      description: 'Internal name of the page.',
    }),
    slug: z.string().nullish().meta({
      description: 'URL slug for the page.',
    }),
    status: z.enum(['published', 'draft', 'archived']).meta({
      description: 'Publication status of the page.',
    }),
    // Optional here although the API always sends them: the MCP server can deploy before the API does.
    testmode: z.boolean().optional().meta({
      description:
        'Whether the page is in test mode. While it is on, every purchase on this page is a test purchase and its records are created with `livemode: false`.',
    }),
    testmodeEnabledAt: z.iso.datetime().nullish().meta({
      description: 'When test mode was switched on. `null` while the page is not in test mode.',
    }),
    images: z.array(MediaResponseValidationSchema).nullish().meta({
      description: 'Images displayed on the page.',
    }),
    locale: z.string().nullish().meta({
      description:
        'Locale the page formats prices and numbers with, for example `en-US` or `de-DE`.',
    }),
    hostedLayoutType: HostedLayoutTypeResponseValidationSchema.nullish(),
    // Optional for the same reason as testmode.
    themeId: z.string().nullish().meta({ description: 'The theme the page uses.' }),
    themeOverrides: ThemeOverridesResponseValidationSchema.nullish(),
    fields: z.array(FieldResponseValidationSchema).nullish().meta({
      description: 'Custom form fields on the page.',
    }),
    // Optional for the same reason as testmode.
    formLayout: PageFormLayoutResponseValidationSchema.optional(),
    url: z.string().nullish().meta({
      description: 'Full URL to the hosted page.',
    }),
    customizeCheckoutConfirmation: z.boolean().nullish().meta({
      description:
        'If set to true `confirmationCheckoutMessage` and `confirmationCheckoutTitle` will be displayed on the confirmation page. Defaults to `false`.',
    }),
    // Embed
    closePopupOnClickOutside: z.boolean().nullish().meta({
      description: 'Whether a popup embed closes when clicking outside the container.',
    }),
    // @TODO use a response schema
    funnelSteps: z
      .array(FunnelStepInputValidationSchema)
      .nullish()
      .meta({
        description:
          'Ordered funnel steps for this page. The response uses the public response structure, including `pageId`, `redirectPageId`, and redirect token `identifier` values.',
        examples: [
          [
            {
              type: 'upsell',
              order: 1,
              enabled: true,
              config: {
                action: 'checkout',
                pageId: '507f1f77bcf86cd799439012',
              },
            },
            {
              type: 'confirmation',
              order: 2,
              enabled: true,
              config: {
                action: 'confirmation',
                customizeCheckoutConfirmation: true,
                confirmationCheckoutTitle: 'Order confirmed',
                confirmationCheckoutMessage: '<p>Thanks for your purchase.</p>',
              },
            },
          ],
        ],
      }),

    visitCount: z.number().nullable().optional().meta({
      description: 'The number of times customers have visited this page.',
    }),
    createdAt: z.iso.datetime().meta({
      description: 'When the page was created.',
    }),
    updatedAt: z.iso.datetime().meta({
      description: 'When the page was last updated.',
    }),
  })
  .extend(RedirectAndConfirmationResponseValidationSchema.shape)
  .extend(NotificationFieldsResponseValidationSchema.shape)
  .meta({ id: 'SharedPage' });
